import { useEffect, useMemo, useState } from "react";
import { Asset } from "expo-asset";
import {
  ActivityIndicator,
  Platform,
  ScrollView,
  Text,
  TextInput,
  View,
} from "react-native";
import type { NavigationProp } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Building2,
  ClipboardList,
  Check,
  MessageCircle,
  Pencil,
  PiggyBank,
  Shield,
  Users,
  CircleCheck,
  ListChecks,
  type LucideIcon,
} from "lucide-react-native";
import {
  CardSurface,
  FieldMessage,
  IconButton,
  MoonDetailRow,
  SectionTitleRow,
  SecondaryButton,
  BackLink,
  CardSubtitleText,
  MoonSkeleton,
} from "@louise/moon-ui";
import { getBlock, makeId } from "@louise/content";
import type { ArticleBody, ContentBlock } from "@louise/content";
import { useReadArticles } from "@louise/favorites";
import {
  formatCurrency,
  useLocale,
  useRegion,
  useTranslations,
  type TranslationFunction,
} from "@src/packages/i18n";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import {
  useStateMandateForState,
  type StateFertilityMandate,
} from "@src/packages/coverage/hooks/useStateMandates";
import { useCoverage } from "@src/packages/coverage/hooks/useCoverage";
import {
  getRepresentativeUsZipCode,
  getUsStateName,
  inferRegionCode,
} from "@src/packages/costs/estimates/locations";
import { EMBER } from "../../../onboarding/theme";
import { useOnboarding } from "../../../onboarding/OnboardingContext";
import type { CostsStackParamList, MainTabParamList } from "../../types";
import RegionGatePlaceholder from "../../components/RegionGatePlaceholder";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP, colors } from "@louise/moon-ui/theme/tokens";
import { useSmartBack } from "../../hooks/useSmartBack";
import EmployerBenefitsModal from "../YouScreen/modals/EmployerBenefitsModal";
import InsuranceInfoModal from "../YouScreen/modals/InsuranceInfoModal";
import EmployerBenefitsCard from "../YouScreen/sections/EmployerBenefitsCard";
import ArticleSectionCard, {
  type Article,
} from "../../components/ArticleSectionCard";
import SpendingAccountCard, {
  type SpendingAccount,
} from "../../components/SpendingAccountCard";
import USStateCoverageMap from "../../components/USStateCoverageMap";
import {
  getStateCoverageGuidance,
  type ResolvedStateCoverageGuidance,
} from "../../components/stateCoverageGuidance";
import { Hero } from "@src/packages/homepage/plan/components/hero/Hero";
import { SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE } from "../../components/ScreenTitle";
import { semanticColors, withAlpha } from "@louise/theme/tokens";

type Props = NativeStackScreenProps<CostsStackParamList, "EmployerBenefits">;

const selectedStateTexture =
  require("@src/apps/moon/assets/images/care-hero-glow-gradient.png") as number;
const selectedStateTextureUri = Asset.fromModule(selectedStateTexture).uri;

function getMandateLabel(mandate: StateFertilityMandate) {
  if (mandate.mandateType === "mandate-to-cover") return "Mandate to cover";
  if (mandate.mandateType === "mandate-to-offer") return "Mandate to offer";
  return "No statewide mandate";
}

type StateCoverageCard = { label: string; value: string; icon: LucideIcon };

function getStateCoverageCards({
  mandate,
  fallbackGuidance,
  locale,
}: {
  mandate: StateFertilityMandate | undefined;
  fallbackGuidance: ResolvedStateCoverageGuidance | null;
  locale: Parameters<typeof formatCurrency>[1];
}): StateCoverageCard[] {
  if (!mandate) {
    return fallbackGuidance
      ? [
          {
            label: "Coverage type",
            icon: Shield,
            value: fallbackGuidance.coverageType,
          },
          {
            label: "Who it covers",
            icon: Users,
            value: fallbackGuidance.whoCovered,
          },
          {
            label: "What may be covered",
            icon: CircleCheck,
            value: fallbackGuidance.included,
          },
          {
            label: "Requirements & limits",
            icon: ListChecks,
            value: fallbackGuidance.requirements,
          },
        ]
      : [];
  }

  const apiCoverageType = getMandateLabel(mandate);
  // The API owns the high-level mandate classification. Curated state
  // guidance can add service-level detail only when it describes the same
  // mandate type; it must not override a newer API result.
  const compatibleFallbackGuidance =
    fallbackGuidance?.coverageType === apiCoverageType
      ? fallbackGuidance
      : null;
  const apiIncluded = [
    mandate.ivfCovered ? "IVF" : null,
    mandate.iuiCovered ? "IUI" : null,
  ].filter(Boolean);
  const apiRequirements: string[] = [];
  const hasNoStatewideMandate = mandate.mandateType === "none";

  if (mandate.requiresDiagnosis) {
    apiRequirements.push("A diagnosis may be required");
  }
  if (mandate.cycleLimit != null) {
    apiRequirements.push(`${mandate.cycleLimit} cycle limit listed`);
  }
  if (mandate.eggRetrievalLimit != null) {
    apiRequirements.push(
      `${mandate.eggRetrievalLimit} egg retrieval limit listed`,
    );
  }
  if (mandate.lifetimeMaxCents != null) {
    apiRequirements.push(
      `${formatCurrency(Math.round(mandate.lifetimeMaxCents / 100), locale)} lifetime maximum listed`,
    );
  }
  apiRequirements.push(...mandate.additionalRequirements.slice(0, 2));

  const requirements = [
    compatibleFallbackGuidance?.requirements,
    ...apiRequirements,
  ].filter(Boolean);

  return [
    {
      label: "Coverage type",
      icon: Shield,
      value: apiCoverageType,
    },
    {
      label: "Who it covers",
      icon: Users,
      value:
        mandate.appliesToEmployerSize ??
        compatibleFallbackGuidance?.whoCovered ??
        (hasNoStatewideMandate
          ? "No state-regulated plan requirement listed"
          : "Some state-regulated plans"),
    },
    {
      label: "What may be covered",
      icon: CircleCheck,
      value:
        compatibleFallbackGuidance?.included ??
        (apiIncluded.length > 0
          ? apiIncluded.join(" and ")
          : "No specific treatment listed"),
    },
    {
      label: "Requirements & limits",
      icon: ListChecks,
      value:
        requirements.length > 0
          ? requirements.join(" ")
          : hasNoStatewideMandate
            ? "Your employer plan may still include fertility care"
            : "No additional limits listed",
    },
  ];
}

const benefitsHeroBackground =
  require("@src/apps/moon/assets/images/care-hero-glow-gradient.png") as number;

const BENEFIT_READING_IDS = [
  "talk-to-hr-about-fertility-benefits",
  "employer-benefits",
  "insurance-fertility",
];

function getBenefitReadings(t: TranslationFunction): Article[] {
  return BENEFIT_READING_IDS.flatMap((slug) => {
    const block = getBlock(makeId(`moon-article-${slug}`));
    if (!block || block.kind !== "article") return [];

    const article = block as ContentBlock<"article">;
    const body = article.body as ArticleBody;
    const teaser = article.subtitle ?? body.sections[0]?.content ?? "";

    return [
      {
        id: article.id,
        title: article.title,
        time: article.readTimeMinutes
          ? t("learn.read_time_minutes", {
              params: { minutes: String(article.readTimeMinutes) },
            })
          : "",
        tag:
          typeof body.introduction === "string"
            ? body.introduction
            : "Benefits",
        teaser: teaser.length <= 90 ? teaser : `${teaser.slice(0, 87)}...`,
        body,
        tags: article.tags,
      },
    ];
  });
}

function StateMandateCard({
  stateCode,
  stateName,
  mandate,
  isLoading,
  locale,
  onOpenStateChat,
  onReviewInsurance,
  onSelectState,
  zipCode,
  zipSource,
  zipDraft,
  isEditingZip,
  onChangeZipCode,
  onStartEditZipCode,
  onSaveZipCode,
  isSavingZip,
  zipError,
}: {
  stateCode: string | null;
  stateName: string | null;
  mandate: StateFertilityMandate | undefined;
  isLoading: boolean;
  locale: Parameters<typeof formatCurrency>[1];
  onOpenStateChat: () => void;
  onReviewInsurance: () => void;
  onSelectState: (stateCode: string) => void;
  zipCode: string;
  zipSource: "profile" | "ip" | "manual" | "state";
  zipDraft: string;
  isEditingZip: boolean;
  onChangeZipCode: (value: string) => void;
  onStartEditZipCode: () => void;
  onSaveZipCode: () => void;
  isSavingZip: boolean;
  zipError: string | null;
}) {
  const [zipWidth, setZipWidth] = useState(0);
  const hasValidZip =
    inferRegionCode("US", isEditingZip ? zipDraft : zipCode) !== null;

  const selectedStateName = stateName ?? stateCode ?? "your state";
  const mandateStateName = mandate?.stateName || selectedStateName;
  const fallbackGuidance = stateCode
    ? getStateCoverageGuidance(stateCode, mandateStateName)
    : null;
  const detailCards = getStateCoverageCards({
    mandate,
    fallbackGuidance,
    locale,
  });
  const shouldShowLoading = isLoading && !fallbackGuidance;

  return (
    <CardSurface style={{ padding: 16, gap: 14 }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <View style={{ alignItems: "center", flex: 1, minWidth: 0, gap: 3 }}>
          <Text
            className="font-dm-sans"
            style={
              {
                fontSize: 17,
                fontWeight: "700",
                color: EMBER.colors.warm,
                ...(Platform.OS === "web" && selectedStateTextureUri
                  ? {
                      backgroundImage: `url("${selectedStateTextureUri}")`,
                      backgroundSize: "100% 100%",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }
                  : {}),
              } as Record<string, unknown>
            }
          >
            {stateName ?? "Choose a state"}
          </Text>
          <View
            style={{
              position: "relative",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 36,
              width: "100%",
            }}
          >
            {isEditingZip ? (
              <TextInput
                autoFocus
                testID="state-coverage-zip-input"
                accessibilityLabel="ZIP code for state coverage"
                value={zipDraft}
                onChangeText={onChangeZipCode}
                onSubmitEditing={onSaveZipCode}
                onLayout={(event) =>
                  setZipWidth(event.nativeEvent.layout.width)
                }
                placeholder="00000"
                placeholderTextColor={EMBER.colors.textFaint}
                keyboardType="number-pad"
                returnKeyType="done"
                maxLength={5}
                autoComplete="postal-code"
                textContentType="postalCode"
                style={{
                  width: 112,
                  minHeight: 32,
                  borderRadius: 12,
                  borderWidth: 1,
                  borderColor: zipError
                    ? withAlpha(semanticColors.status.danger, 0.44)
                    : withAlpha(semanticColors.text.secondary, 0.21),
                  backgroundColor: semanticColors.surface.input,
                  paddingHorizontal: 8,
                  color: colors.greyBrown,
                  fontSize: 20,
                  lineHeight: 25,
                  fontWeight: "700",
                  fontFamily: "DMMono_400Regular",
                  textAlign: "center",
                }}
              />
            ) : (
              <Text
                className="font-dm-mono"
                onLayout={(event) =>
                  setZipWidth(event.nativeEvent.layout.width)
                }
                style={{
                  fontSize: 20,
                  lineHeight: 25,
                  fontWeight: "700",
                  color: colors.greyBrown,
                }}
              >
                {zipCode || "—"}
              </Text>
            )}
            <IconButton
              testID={
                isEditingZip
                  ? "state-coverage-zip-save"
                  : "state-coverage-zip-edit"
              }
              accessibilityLabel={`${isEditingZip ? "Save" : "Edit"} ZIP code`}
              action={isEditingZip ? "save_zip_code" : "edit_zip_code"}
              onPress={() => {
                if (isEditingZip) onSaveZipCode();
                else onStartEditZipCode();
              }}
              disabled={isEditingZip && (!hasValidZip || isSavingZip)}
              hitSlop={8}
              size={20}
              // The pencil rests at a muted 0.48, which IconButton's own
              // opacity states (always 1 at rest) cannot express.
              style={({ pressed }) => ({
                position: "absolute",
                left: "50%",
                marginLeft: (zipWidth || (isEditingZip ? 112 : 40)) / 2 + 8,
                opacity:
                  isEditingZip && (!hasValidZip || isSavingZip)
                    ? 0.3
                    : pressed
                      ? 0.7
                      : 0.48,
              })}
            >
              {isSavingZip ? (
                <ActivityIndicator size="small" color={colors.greyBrown} />
              ) : isEditingZip ? (
                <Check size={13} color={colors.greyBrown} strokeWidth={2.4} />
              ) : (
                <Pencil size={12} color={colors.greyBrown} strokeWidth={2.1} />
              )}
            </IconButton>
          </View>
          {zipSource === "manual" || zipSource === "profile" ? (
            <Text
              className="font-dm-sans"
              style={{ fontSize: 9, color: EMBER.colors.textFaint }}
            >
              {zipSource === "manual"
                ? "Updated by you"
                : "From your saved location"}
            </Text>
          ) : null}
        </View>
      </View>

      <USStateCoverageMap
        selectedStateCode={stateCode}
        onSelect={onSelectState}
      />

      {zipError ? <FieldMessage>{zipError}</FieldMessage> : null}

      {shouldShowLoading || isSavingZip ? (
        <View
          testID="state-coverage-loading"
          accessibilityLabel="Loading state coverage details"
          style={{ gap: 10 }}
        >
          <MoonSkeleton width="42%" height={16} radius={999} />
          <MoonSkeleton width="88%" height={12} radius={999} />
          <View style={{ flexDirection: "row", gap: 8 }}>
            {["state-coverage-skeleton-one", "state-coverage-skeleton-two"].map(
              (skeletonId) => (
                <View key={skeletonId} testID={skeletonId} style={{ flex: 1 }}>
                  <MoonSkeleton width="100%" height={76} radius={12} />
                </View>
              ),
            )}
          </View>
        </View>
      ) : !stateCode ? null : (
        <View style={{ gap: 12 }}>
          <View style={{ gap: 14 }}>
            {detailCards.map((detail) => (
              <MoonDetailRow
                key={detail.label}
                icon={detail.icon}
                label={detail.label}
                value={
                  <CardSubtitleText style={{ marginTop: 0 }}>
                    {detail.value}
                  </CardSubtitleText>
                }
              />
            ))}
          </View>

          <View style={{ flexDirection: "row", gap: 8 }}>
            <SecondaryButton
              label="Chat about this"
              onPress={onOpenStateChat}
              leading={
                <MessageCircle
                  size={16}
                  color={EMBER.colors.textMid}
                  strokeWidth={1.8}
                />
              }
              flex={1}
            />
            <SecondaryButton
              label="Review my insurance"
              onPress={onReviewInsurance}
              leading={
                <Shield
                  size={16}
                  color={EMBER.colors.textMid}
                  strokeWidth={1.8}
                />
              }
              flex={1}
            />
          </View>
        </View>
      )}
    </CardSurface>
  );
}

export default function EmployerBenefitsScreen({ navigation, route }: Props) {
  const rootNavigation =
    navigation.getParent<NavigationProp<MainTabParamList>>();
  const region = useRegion();
  const { locale } = useLocale();
  const t = useTranslations();
  const { data: onboardingData, updateField } = useOnboarding();
  const { data: patient } = usePatient();
  const {
    primaryPolicy,
    hasInsurance,
    verificationStatus,
    fertilityCoverage,
    isLoading: isInsuranceLoading,
  } = useCoverage();
  const [employerModalVisible, setEmployerModalVisible] = useState(false);
  const [insuranceModalVisible, setInsuranceModalVisible] = useState(false);
  const benefitReadings = useMemo(() => {
    void locale;
    return getBenefitReadings(t);
  }, [locale, t]);
  const { isRead, markAsRead } = useReadArticles();
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Costs",
    fallbackScreen: "CostsOverview",
  });

  const onboardingStateCode = useMemo(
    () =>
      inferRegionCode(
        patient?.addresses?.[0]?.country ?? onboardingData.country,
        patient?.addresses?.[0]?.postalCode ?? onboardingData.zipCode,
      ),
    [onboardingData.country, onboardingData.zipCode, patient?.addresses],
  );
  const [stateCodeOverride, setStateCodeOverride] = useState<string | null>(
    null,
  );
  const stateCode = stateCodeOverride ?? onboardingStateCode;
  const stateName = stateCode ? (getUsStateName(stateCode) ?? stateCode) : null;
  const mandateQuery = useStateMandateForState(stateCode);
  const profileZipCode =
    (patient?.addresses?.[0]?.postalCode ?? onboardingData.zipCode ?? "").match(
      /\d{5}/,
    )?.[0] ?? "";
  const [zipCode, setZipCode] = useState("");
  const [zipDraft, setZipDraft] = useState("");
  const [zipSource, setZipSource] = useState<
    "profile" | "ip" | "manual" | "state"
  >("profile");
  const [isEditingZip, setIsEditingZip] = useState(false);
  const [zipError, setZipError] = useState<string | null>(null);
  const [isSavingZip, setIsSavingZip] = useState(false);

  useEffect(() => {
    if (!stateCode) return;

    const currentZipState = inferRegionCode("US", zipCode);
    if (currentZipState === stateCode) return;

    const profileStateCode = inferRegionCode("US", profileZipCode);
    const nextZipCode =
      profileStateCode === stateCode
        ? profileZipCode
        : getRepresentativeUsZipCode(stateCode);
    if (!nextZipCode) return;

    setZipCode(nextZipCode);
    setZipDraft(nextZipCode);
    setZipSource(profileStateCode === stateCode ? "profile" : "state");
  }, [profileZipCode, stateCode, zipCode]);

  useEffect(() => {
    if (Platform.OS !== "web" || profileZipCode || onboardingStateCode) return;

    let isMounted = true;
    fetch("https://ipwho.is/")
      .then(async (response) => {
        if (!response.ok) return null;
        return (await response.json()) as {
          success?: boolean;
          country_code?: string;
          postal?: string | null;
        };
      })
      .then((location) => {
        if (
          !isMounted ||
          !location?.success ||
          location.country_code !== "US"
        ) {
          return;
        }
        const detectedZip = location.postal?.match(/\d{5}/)?.[0];
        if (!detectedZip) return;
        const detectedStateCode = inferRegionCode("US", detectedZip);
        setZipCode((current) => current || detectedZip);
        setZipDraft((current) => current || detectedZip);
        setZipSource("ip");
        if (detectedStateCode) {
          setStateCodeOverride((current) => current ?? detectedStateCode);
        }
      })
      .catch(() => undefined);

    return () => {
      isMounted = false;
    };
  }, [onboardingStateCode, profileZipCode]);

  useEffect(() => {
    if (!isSavingZip) return;
    const timeoutId = setTimeout(() => setIsSavingZip(false), 450);
    return () => clearTimeout(timeoutId);
  }, [isSavingZip]);

  const openEmployerModal = () => setEmployerModalVisible(true);
  const openBenefitReading = (article: Article) => {
    markAsRead(article.id);
    rootNavigation?.navigate("Learn", {
      screen: "ArticleDetail",
      params: {
        articleId: article.id,
        returnTab: "Costs",
        utm_source: "costs",
      },
    });
  };
  const openSpendingAccountChat = (account: SpendingAccount) => {
    rootNavigation?.navigate("Chat", {
      screen: "Assistant",
      params: {
        initialPrompt: `I want to use my ${account} dollars for fertility care. Help me figure out what I can submit or reimburse. First check whether I have already uploaded an itemized bill, receipt, explanation of benefits, or plan document. Ask me for photos or uploads for anything missing, then help me identify what to confirm with my benefits administrator before I pay or submit it.`,
        autoSendInitialPrompt: true,
        returnTab: "Costs",
      },
    });
  };
  const openStateCoverageChat = () => {
    const selectedState = stateName ?? "my state";
    rootNavigation?.navigate("Chat", {
      screen: "Assistant",
      params: {
        initialPrompt: `I want to understand fertility coverage in ${selectedState}. Explain the statewide rule in plain language, help me understand what may or may not apply to my employer plan, and give me a short list of questions to ask my insurer. Ask me for my plan documents if they would help confirm the details.`,
        autoSendInitialPrompt: true,
        returnTab: "Costs",
      },
    });
  };
  const openInsurance = () => setInsuranceModalVisible(true);
  const openZipEditor = () => {
    setZipDraft(zipCode);
    setZipError(null);
    setIsEditingZip(true);
  };
  const handleZipCodeChange = (value: string) => {
    setZipDraft(value.replace(/\D/g, "").slice(0, 5));
    if (zipError) setZipError(null);
  };
  const saveZipCode = () => {
    const nextZipCode = zipDraft;
    const nextStateCode = inferRegionCode("US", nextZipCode);
    if (nextStateCode === null) {
      setZipError("Enter a valid 5-digit U.S. ZIP code.");
      return;
    }

    setZipError(null);
    setStateCodeOverride(nextStateCode);
    setIsSavingZip(true);
    setIsEditingZip(false);
    setZipCode(nextZipCode);
    setZipSource("manual");
    updateField("country", "US");
    updateField("zipCode", nextZipCode);
  };
  const selectState = (nextStateCode: string) => {
    setStateCodeOverride(nextStateCode);
    setZipError(null);

    const representativeZipCode = getRepresentativeUsZipCode(nextStateCode);
    if (
      representativeZipCode &&
      inferRegionCode("US", zipCode) !== nextStateCode
    ) {
      setZipCode(representativeZipCode);
      setZipDraft(representativeZipCode);
      setZipSource("state");
    }
  };

  if (region !== "US") return <RegionGatePlaceholder />;

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <BackLink
          label={route.params?.returnTab ? t("you.shared.back") : "Costs"}
          accessibilityLabel={
            route.params?.returnTab
              ? t("you.shared.back")
              : t("costs.back_to_costs")
          }
          onPress={handleBack}
          style={{ marginBottom: 8 }}
        />

        <Text
          className="font-dm-sans"
          style={
            {
              fontSize: 38,
              fontWeight: "700",
              color: colors.deepBrown,
              lineHeight: 42,
              letterSpacing: -0.5,
              ...(Platform.OS === "web" && {
                ...SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }),
            } as Record<string, unknown>
          }
        >
          Benefits
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textLight,
            marginTop: 2,
            marginBottom: 24,
          }}
        >
          See what your employer and state may offer, and what to ask next.
        </Text>

        <View style={{ gap: 26 }}>
          <Hero
            backgroundSource={benefitsHeroBackground}
            eyebrow={{ label: "Your coverage", icon: Shield }}
            title="Your benefits at a glance"
            summary="Check what’s on file, what your state may require, and what to review next."
          />

          <EmployerBenefitsCard
            hasEmployerBenefits={
              patient?.fertilityIntent?.hasEmployerBenefits ?? null
            }
            employerBenefit={patient?.fertilityIntent?.employerBenefit ?? null}
            bottomSpacing={0}
            onPress={openEmployerModal}
          />

          <View>
            <SectionTitleRow title="Some light reading" icon={ClipboardList} />
            <ArticleSectionCard
              articles={benefitReadings}
              isRead={isRead}
              onOpenArticle={openBenefitReading}
              topSpacing={0}
            />
          </View>

          <View>
            <SectionTitleRow title="State coverage" icon={Building2} />
            <StateMandateCard
              stateCode={stateCode}
              stateName={stateName}
              mandate={mandateQuery.data}
              isLoading={mandateQuery.isLoading}
              locale={locale}
              onOpenStateChat={openStateCoverageChat}
              onReviewInsurance={openInsurance}
              onSelectState={selectState}
              zipCode={zipCode}
              zipSource={zipSource}
              zipDraft={zipDraft}
              isEditingZip={isEditingZip}
              onChangeZipCode={handleZipCodeChange}
              onStartEditZipCode={openZipEditor}
              onSaveZipCode={saveZipCode}
              isSavingZip={isSavingZip}
              zipError={zipError}
            />
          </View>

          <View>
            <SectionTitleRow title="HSA / FSA" icon={PiggyBank} />
            <SpendingAccountCard
              locale={locale}
              onOpenChat={openSpendingAccountChat}
            />
          </View>
        </View>
      </ScrollView>

      <InsuranceInfoModal
        visible={insuranceModalVisible}
        onClose={() => setInsuranceModalVisible(false)}
        primaryPolicy={primaryPolicy}
        hasInsurance={hasInsurance}
        verificationStatus={verificationStatus}
        fertilityCoverage={fertilityCoverage}
        isLoading={isInsuranceLoading}
        initialMode="edit"
      />
      <EmployerBenefitsModal
        visible={employerModalVisible}
        onClose={() => setEmployerModalVisible(false)}
      />
    </GlassScreenLayout>
  );
}
